import React from "react";

import { useFx } from "../../../hooks/fx";
import { resolveOriginalMoney } from "../../../core/currency";
import { Money } from "../../../components";

/** The listing's own cost fields, in `costs_currency`. */
const LISTING_COST_FIELDS = ["polishing", "accessories", "shipping", "travel"];

const NetProfit = ({
  type,
  buy,
  buyCurrency,
  sell,
  sellCurrency,
  market,
  trade,
  avgCurrency,
  listing,
  quote,
}) => {
  // Each rate is null when no rate is known (request failed and nothing was
  // cached). Then the figure is hidden, never computed 1:1 — see FX RULE in
  // hooks/fx.js.
  const { rate, isLoading, unavailable } = useFx(
    ...(type === "order"
      ? [buyCurrency || "gbp", sellCurrency || "gbp"]
      : [avgCurrency || "gbp", buyCurrency || "gbp"])
  );

  const costsCurrency = quote?.costs?.costs_currency || "gbp";
  const { rate: costsRate, isLoading: costLoading, unavailable: costsUnavailable } = useFx(
    costsCurrency,
    type === "order" ? (sellCurrency || "gbp") : (buyCurrency || "gbp")
  );

  // Listing fallback costs (used when the quote has no costs of its own).
  // Prefer the ORIGINAL amounts + costs_currency: the gbp_* pivots were
  // produced by converting those originals INTO GBP, so converting a pivot
  // back out is a second conversion and drifts the value (a cost entered as
  // 1000 USD displayed as 993). resolveOriginalMoney returns amount and
  // currency together, falling back to the pivot only for legacy rows that
  // never had a currency stamped.
  const listingCosts = resolveOriginalMoney(
    listing,
    LISTING_COST_FIELDS,
    "costs_currency"
  );
  const { rate: listingCostsRate, unavailable: listingCostsUnavailable } = useFx(
    listingCosts?.currency || "gbp",
    type === "order" ? (sellCurrency || "gbp") : (buyCurrency || "gbp")
  );

  const totalCosts = (() => {
    if (quote?.costs) {
      const polishing = ((quote?.costs?.polishing || 0) ) * costsRate;
      const accessories = ((quote?.costs?.accessories || 0) ) * costsRate;
      const shipping = ((quote?.costs?.shipping || 0) ) * costsRate;
      const travel = ((quote?.costs?.travel || 0) ) * costsRate;

      return Math.round((polishing + accessories + shipping + travel));
    } else {
      return Math.round((listingCosts?.amount || 0) * listingCostsRate);
    }
  })();

  if (
    isLoading ||
    costLoading ||
    (type === "order" && (!sell || !buy)) ||
    (type === "sell" && !buy && !(market || trade))
  ) {
    return <span>💰</span>;
  }

  // A cost rate only matters when there is a cost to convert.
  const costsNeedRate = quote?.costs
    ? ["polishing", "accessories", "shipping", "travel"].some(
        (k) => !!quote.costs[k]
      )
    : !!listingCosts?.amount;
  const costsRateUsed = quote?.costs ? costsRate : listingCostsRate;
  if (rate == null || (costsNeedRate && costsRateUsed == null)) {
    const failed =
      unavailable ||
      (costsNeedRate &&
        (quote?.costs ? costsUnavailable : listingCostsUnavailable));
    return failed ? (
      <span className="text-muted" title="Exchange rate unavailable">
        Rate unavailable
      </span>
    ) : (
      <span>💰</span>
    );
  }

  if (type === "order") {
    const profit = Math.round(parseFloat(sell) - parseFloat(buy * rate));
    const netProfit = profit - totalCosts;

    return (
      <Money
        amount={netProfit}
        currency={sellCurrency?.toLowerCase()}
        className="money"
      />
    );
  }

  const minProfit =
    trade && Math.round(parseFloat(trade * rate) - parseFloat(buy));
  const maxProfit =
    market && Math.round(parseFloat(market * rate) - parseFloat(buy));

  const minNetProfit = minProfit ? minProfit - totalCosts : null;
  const maxNetProfit = maxProfit ? maxProfit - totalCosts : null;

  return (
    <span className="profit-range">
      {trade && buy ? (
        <Money
          amount={minNetProfit}
          currency={buyCurrency?.toLowerCase()}
          className="money"
        />
      ) : (
        <span>💰</span>
      )}
      &nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;
      {market && buy ? (
        <Money
          amount={maxNetProfit}
          currency={buyCurrency?.toLowerCase()}
          className="money"
        />
      ) : (
        <span>💰</span>
      )}
    </span>
  );
};

export default NetProfit;
